import { useState } from 'react'
import { Helmet } from 'react-helmet'
import { useParams } from 'react-router'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Skeleton, SkeletonGroup } from '@/components/ui/skeleton'
import { usePublicClaim, useUploadPublicClaimPhoto } from '../../models/claim'
import { ClaimPhotoUploader } from '../Private/App/Claims/ClaimPhotoUploader'

const photoKey = (file: File) => `${file.name}:${file.size}`

export function ClaimPhotosPage() {
  const { claimId } = useParams<{ claimId: string }>()
  const { data, isLoading, isError } = usePublicClaim(claimId)
  const upload = useUploadPublicClaimPhoto(claimId)
  const [photos, setPhotos] = useState<File[]>([])
  const [submitting, setSubmitting] = useState(false)
  // Keys of files already uploaded this session, so re-submitting only sends new ones.
  const [uploadedKeys, setUploadedKeys] = useState<Set<string>>(() => new Set())

  const pending = photos.filter((f) => !uploadedKeys.has(photoKey(f)))
  const allSubmitted = photos.length > 0 && pending.length === 0

  async function handleSubmit() {
    if (pending.length === 0) return
    setSubmitting(true)
    try {
      // Track each upload independently so a partial failure still records the
      // photos that did upload — a retry then only re-sends the ones that failed.
      const results = await Promise.allSettled(pending.map((f) => upload.mutateAsync({ file: f })))
      const succeeded = pending.filter((_, i) => results[i].status === 'fulfilled')
      if (succeeded.length > 0) {
        setUploadedKeys((prev) => new Set([...prev, ...succeeded.map(photoKey)]))
      }
      const failed = pending.length - succeeded.length
      if (failed > 0) {
        toast.error(`${failed} file${failed !== 1 ? 's' : ''} failed to upload. Please try again.`)
      } else {
        toast.success(`${succeeded.length} file${succeeded.length !== 1 ? 's' : ''} submitted.`)
      }
    } finally {
      setSubmitting(false)
    }
  }

  if (isLoading)
    return (
      <SkeletonGroup className="mx-auto flex w-full max-w-2xl flex-col gap-6 p-4 pt-12 sm:pt-20">
        <div className="flex flex-col gap-2">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-5 w-full max-w-md" />
        </div>
        <Skeleton className="h-48 w-full rounded-xl" />
        <Skeleton className="ml-auto h-10 w-32" />
      </SkeletonGroup>
    )
  if (isError || !data)
    return <div className="p-8 text-center">This claim link is invalid or has expired.</div>

  return (
    <>
      <Helmet>
        <title>Upload claim photos</title>
      </Helmet>

      <div className="mx-auto flex w-full max-w-2xl flex-col gap-6 p-4 pt-12 sm:pt-20">
        <div>
          <h1 className="text-2xl font-bold">Upload Photos</h1>
          <p className="text-muted-foreground text-md">
            Claim {data.idShort}. Add photos or documents from the scene so we can process your claim.
          </p>
        </div>
        <ClaimPhotoUploader files={photos} onChange={setPhotos} uploaded={allSubmitted} />
        <div className="flex justify-end">
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={pending.length === 0}
            loading={submitting ? 'Submitting…' : undefined}
          >
            {allSubmitted ? 'Files submitted' : 'Submit files'}
          </Button>
        </div>
      </div>
    </>
  )
}
